import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './toast.css';

type ToastAction = { label: string; onClick: () => void };

type Toast = {
  id: number;
  message: string;
  action?: ToastAction;
  dismissible?: boolean;
};

type ShowOptions = {
  action?: ToastAction;
  /** Auto-dismiss after this many ms. Defaults to 8000. Pass 0 to keep it up until acted on. */
  durationMs?: number;
  /** Render an explicit dismiss (×) control. Useful for persistent toasts. */
  dismissible?: boolean;
};

type ToastContextValue = {
  show: (message: string, opts?: ShowOptions) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 8000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<number | null>(null);
  const nextId = useRef(1);

  const dismiss = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setToast(null);
  }, []);

  const show = useCallback((message: string, opts?: ShowOptions) => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const id = nextId.current++;
    const dismissible = opts?.dismissible ?? false;
    setToast({ id, message, action: opts?.action, dismissible });
    const duration = opts?.durationMs ?? DEFAULT_DURATION_MS;
    // duration 0 → persistent (e.g. the new-version prompt): it stays until the
    // user takes the action or dismisses it, rather than vanishing on a timer.
    if (duration > 0) {
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setToast((cur) => (cur?.id === id ? null : cur));
      }, duration);
    }
  }, []);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {toast && (
        <div className="toast" role="status" aria-live="polite">
          <span className="toast__msg">{toast.message}</span>
          {toast.action && (
            <button
              className="toast__action"
              onClick={() => {
                toast.action!.onClick();
                dismiss();
              }}
            >
              {toast.action.label}
            </button>
          )}
          {toast.dismissible && (
            <button
              className="toast__dismiss"
              aria-label={t('common.close')}
              onClick={dismiss}
            >
              ×
            </button>
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
